import React, { useEffect, useState, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// PUBLIC SITE: No MSAL imports - this is an unauthenticated public site
import { ProductProvider, useAuthState, getAuthPortalUrl } from 'coreIndex';
import { useUserType, VIEW_MODES } from 'utils/userTypeContext';
import { logger } from 'utils/logger';
import { ToastContainer } from 'feSharedIndex';
import { Header, Footer } from 'coreIndex';
import { BottomNavigation, Logger, LanguageRedirect } from 'coreIndex';
import AuthOnlyRedirect from './components/AuthOnlyRedirect';
import {
  PublicAuthProvider,
  PublicProfileProvider,
  PublicUserTypeProvider,
  PublicTenantProvider
} from 'contexts/publicAppProviders';
// Register page-specific translations
import { registerPageTranslations } from 'utils/translationLoader';
import { PAGE_TRANSLATIONS_MAP } from './utils/translationLoader';
// ForcedLegalAgreementModal removed — onboarding/legal modal only lives in platform-auth
import { isLegalPage } from 'utils/legalAgreementsGuard';
import { normalizeLegalAgreementsFromApi } from 'utils/normalizeLegalAgreements';
import UserTypeSelectionModal from './components/UserTypeSelectionModal/UserTypeSelectionModal';
// Besper SDK styles are bundled in the JavaScript - no separate CSS import needed
import { displayStartupLogo } from 'utils/logoDisplay';
import HomePage from './pages/homepage/HomePage';
// Dashboard - TODO: Create if needed
// import Dashboard from './pages/dashboard/Dashboard/Dashboard';
// ============================================================================
// AUTHENTICATED PAGES REMOVED - These should only exist in platform-auth
// ============================================================================
// UserTenantMappingManagement - MIGRATED to platform-auth
// SecurityRolesManagement - MIGRATED to platform-auth
// ResourceManagement - MIGRATED to platform-auth
// AdvancedResourceManagement - MIGRATED to platform-auth
// ============================================================================
// Auth-specific pages - these belong in platform-auth, not platform-public
// import BotManagement from './pages/bot-resource-management';
// import ResaleManagement from './pages/product/ResaleManagement/ResaleManagement';
// import FinancialEducationManagement from './pages/product/FinancialEducationManagement/FinancialEducationManagement';
// import FinEduManagement from './pages/product/FinEduManagement/FinEduManagement';
import Roadmap from './pages/product/Roadmap/Roadmap';
// import ResourcePage from './pages/resourcepage/ResourcePage/ResourcePage';
// import UserProfile from './pages/profile/ProfileManagement';
// import ApplicationUserManagement from './pages/application-user-management/ApplicationUserManagement';
// import PaymentProfilePage from './pages/payment-profile/PaymentProfilePage';
// import AuthCallback from './pages/authcallback/AuthCallback/AuthCallback';
// import AuthReset from './pages/auth/AuthReset';
// import LoggedOut from './pages/auth/LoggedOut';
// import CommunicationsCenter from './pages/communicationscenter/CommunicationsCenter/CommunicationsCenter';
import AboutUs from './pages/about/AboutUs/AboutUs';
import { TechnicalDocumentation } from 'coreIndex';
// Legal pages
import TermsOfService from './pages/terms/TermsOfService/TermsOfService';
import PrivacyPolicy from './pages/privacy/PrivacyPolicy/PrivacyPolicy';
import Impressum from './pages/impressum/Impressum/Impressum';
import Changelog from './pages/changelog/Changelog/Changelog';
// Legal pages
import CookieSettings from './pages/cookies/CookieSettings/CookieSettings';
import GDPR from './pages/gdpr/GDPR/GDPR';
import DataProcessingAgreement from './pages/dpa/DataProcessingAgreement/DataProcessingAgreement';
import BusinessDataProcessingAgreement from './pages/businessdpa/BusinessDataProcessingAgreement/BusinessDataProcessingAgreement';
import Press from './pages/press/Press/Press';
import Investors from './pages/investors/Investors/Investors';
import BesperAi from './pages/besper-ai/BesperAi/BesperAi';
import Newsletter from './pages/newsletter/Newsletter/Newsletter';
import Events from './pages/events/Events/Events';
import Strategy from './pages/strategy/Strategy/Strategy';
import Architecture from './pages/architecture/Architecture/Architecture';
import Runtime from './pages/architecture/Runtime/Runtime';
import Gateway from './pages/architecture/Gateway/Gateway';
import Compute from './pages/architecture/Compute/Compute';
import Database from './pages/architecture/Database/Database';
import ArchitectureComponents from './pages/architecture/ArchitectureComponents/ArchitectureComponents';
import Processes from './pages/architecture/Processes/Processes';
import CustomerPortalAPIs from './pages/architecture/Processes/CustomerPortalAPIs/CustomerPortalAPIs';
import CustomerPortalApiGroup from './pages/architecture/Processes/CustomerPortalAPIs/CustomerPortalApiGroup';
import CustomerPortalEndpoint from './pages/architecture/Processes/CustomerPortalAPIs/endpoints/CustomerPortalEndpoint';
import PublicAPIs from './pages/architecture/Processes/PublicAPIs/PublicAPIs';
import ResourcesEndpoint from './pages/architecture/Processes/CustomerPortalAPIs/endpoints/Resources';
import BotOperationsEndpoint from './pages/architecture/Processes/PublicAPIs/endpoints/BotOperations';
import Partners from './pages/partners/Partners/Partners';
import { Marketplace } from 'coreIndex';
import Testimonials from './pages/testimonials/Testimonials/Testimonials';
import ServiceLevelAgreement from './pages/servicelevelagreement/ServiceLevelAgreement/ServiceLevelAgreement';
import Status from './pages/status/Status/Status';
// Legal pages
import EULA from './pages/eula/EULA/EULA';
import AcceptableUse from './pages/acceptableuse/AcceptableUse/AcceptableUse';
import Disclaimer from './pages/disclaimer/Disclaimer/Disclaimer';
// Confidential pages - moved to platform-auth
// import PartnerPricing from './pages/partnerpricing/PartnerPricing/PartnerPricing';
// import PartnerPitchDeck from './pages/partnerpitchdeck/PartnerPitchDeck/PartnerPitchDeck';
import { ErrorBoundary } from 'feSharedIndex';
import PageGuards from './pages/shared/onload/PageGuards';
// Styling Mock Pages
import StylingMockIndex from './pages/styling-mock/StylingMockIndex/StylingMockIndex';
import StylingMockButtons from './pages/styling-mock/StylingMockButtons/StylingMockButtons';
import StylingMockContainers from './pages/styling-mock/StylingMockContainers/StylingMockContainers';
import StylingMockUI from './pages/styling-mock/StylingMockUI/StylingMockUI';
import StylingMockTypography from './pages/styling-mock/StylingMockTypography/StylingMockTypography';
import StylingMockLogo from './pages/styling-mock/StylingMockLogo/StylingMockLogo';
import StylingMockLayouts from './pages/styling-mock/StylingMockLayouts/StylingMockLayouts';
import StylingMockAnimations from './pages/styling-mock/StylingMockAnimations/StylingMockAnimations';
import StylingMockNavigation from './pages/styling-mock/StylingMockNavigation/StylingMockNavigation';
import FunIndex from './pages/fun/FunIndex/FunIndex';
import Wichteln from './pages/fun/wichteln/Wichteln';
import Blog from './pages/blog/Blog/Blog';
import { I18nProvider } from 'utils/i18n';
import { DarkModeProvider } from 'utils/darkMode';
import { initializePermissions } from 'utils/permissions';
import { initializeApiClient } from 'utils/apiClient';
import ReauthRequired from './components/auth/ReauthRequired';

import RegionProviderModal from 'components/auth/RegionProviderModal';
import PublicPageRedirect from 'components/auth/PublicPageRedirect';
import { isAuthPortal, parseDomain, mapRegionCodeToName } from 'utils/domainParser';

// PUBLIC SITE: No MSAL config imports - this is an unauthenticated public site
// eslint-disable-next-line no-unused-vars
import { getConfig } from 'utils/centralizedConfig';

// Register all page translations on app startup (after all imports)
registerPageTranslations(PAGE_TRANSLATIONS_MAP);

// Global Bot Widget - Uses mode-aware loader to avoid conflicts with container embeds
/**
 * BottomNavigationWrapper Component
 * Detects auth state and view mode to show appropriate bottom navigation
 */
function BottomNavigationWrapper({ viewMode, onSignIn }) {
  const { isAuthenticated } = useAuthState();
  const location = useLocation();

  // Determine if user is authenticated
  const accounts = null || [];
  const isLoggedIn = isAuthenticated || accounts.length > 0;

  // Determine variant based on auth state and view mode
  let variant = 'public';
  if (isLoggedIn) {
    if (viewMode === VIEW_MODES.TECHNICAL) {
      variant = 'technical';
    } else {
      variant = 'customer';
    }
  }

  // Don't show on auth callback pages or certain routes
  const hideOnRoutes = ['/auth/callback', '/auth/reset', '/logged-out'];
  const shouldHide = hideOnRoutes.some(route => location.pathname.includes(route));

  if (shouldHide) {
    return null;
  }

  return <BottomNavigation variant={variant} onSignIn={onSignIn} />;
}

function AppContent() {
  if (process.env.NODE_ENV === 'development' || (typeof window !== 'undefined' && window.location && window.location.search && window.location.search.includes('ppdebug'))) {
    console.log('[PP-DBG] 5 AppContent render start');
  }
  // PUBLIC SITE: No MSAL/auth - this is an unauthenticated public site
  const location = useLocation();
  const isConfidentialPage = /^\/(?:[a-z]{2}\/)?(?:partner-pricing|partner-pitch-deck)(?:\/|$)/.test(location.pathname);
  const isBotManagementPage = location.pathname.includes('/bot-management');
  
  // CRITICAL: Check if we're on auth portal and need to show modal immediately
  // This check runs synchronously before any routes render
  // CRITICAL: Port 4001 is the public site - NEVER show RegionProviderModal there
  const [showRegionProviderModal, setShowRegionProviderModal] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    // CRITICAL: Explicitly prevent modal on port 4001 (public site)
    // Port 4001 should NEVER show RegionProviderModal
    if (port === '4001' || parseInt(port, 10) === 4001) {
      return false;
    }
    
    const isOnAuthPortal = isAuthPortal(hostname);
    
    // Only show modal if on auth portal (port 4002)
    if (!isOnAuthPortal) {
      return false;
    }
    
    // Check if region/provider is already selected
    const selectedRegion = typeof localStorage !== 'undefined' ? localStorage.getItem('selectedRegion') : null;
    const selectedProvider = typeof localStorage !== 'undefined' ? localStorage.getItem('selectedProvider') : null;
    
    // Show modal if region/provider not selected
    if (!selectedRegion || !selectedProvider) {
      logger.info('APP', 'Initial modal check: Showing region/provider modal', {
        hostname,
        port,
        isOnAuthPortal,
        selectedRegion,
        selectedProvider
      });
      return true;
    }
    
    return false;
  });

  // Defer full content until after first paint to avoid sync recursion (RangeError: Maximum call stack size exceeded)
  const [appContentReady, setAppContentReady] = useState(false);
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' || (typeof window !== 'undefined' && window.location && window.location.search && window.location.search.includes('ppdebug'))) {
      console.log('[PP-DBG] 8 AppContent useEffect (appContentReady) scheduling rAF');
    }
    const id = requestAnimationFrame(() => {
      if (process.env.NODE_ENV === 'development' || (typeof window !== 'undefined' && window.location && window.location.search && window.location.search.includes('ppdebug'))) {
        console.log('[PP-DBG] 9 AppContent rAF fired, setting appContentReady=true');
      }
      setAppContentReady(true);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  // Mobile detection - used for conditional rendering
  const [isMobile, setIsMobile] = useState(() => {
    // Check on initial render
    return window.innerWidth <= 768 ||
           /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
  });

  const handleSignIn = useCallback(() => {
    let authPortalUrl = typeof getAuthPortalUrl === 'function' ? getAuthPortalUrl() : null;
    if (!authPortalUrl) {
      const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
      logger.error('APP', 'Sign in: AUTH_PORTAL_URL / CUSTOMER_DOMAIN not set. Redirect disabled.', { hostname });
      return;
    }
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const port = typeof window !== 'undefined' ? window.location.port : '';
    const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
    if ((hostname === 'localhost' || hostname === '127.0.0.1') && port === '5003' && authPortalUrl.includes('4003')) {
      authPortalUrl = `${protocol}//${hostname}:5004`;
      if (process.env.NODE_ENV !== 'production') {
        logger.warn('APP', 'Sign in: public is on 5003 but auth URL pointed to 4003; using 5004 so auth portal is reachable.', { authPortalUrl });
      }
    }
    if (process.env.NODE_ENV !== 'production') {
      logger.info('APP', 'Sign in redirect', { authPortalUrl, currentPort: port });
    }
    window.location.href = authPortalUrl;
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      return window.innerWidth <= 768 || 
             /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
             (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
    };
    
    const handleResize = () => {
      setIsMobile(checkMobile());
    };
    
    // Check on mount and resize
    setIsMobile(checkMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // PUBLIC SITE: No login request creation - this is an unauthenticated public site
  // Removed all MSAL login request code
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUserTypeSelection, setShowUserTypeSelection] = useState(false);
  const [, setShowForcedLegalModal] = useState(false);
  // showOnboarding kept as dead-code compatibility for unreachable compliance check branches
  // eslint-disable-next-line no-unused-vars
  const [showOnboarding, setShowOnboarding] = useState(false);
  // showRegionProviderModal is now initialized above with synchronous check
  const [userProfile, setUserProfile] = useState(null);
  const [reauthError, setReauthError] = useState(null);
  const { viewMode, isLoading: userTypeLoading } = useUserType();
  const reauthErrorCooldownRef = useRef(0); // Cooldown to prevent multiple modals
  const profileLoadedRef = useRef(false);
  const loadingProfileRef = useRef(false);
  const legalComplianceCheckRef = useRef(false); // Track if we've checked compliance
  const lastCheckedPathnameRef = useRef(null); // Track last checked pathname to prevent duplicate checks
  const complianceCheckInProgressRef = useRef(false); // Track if check is currently running
  // eslint-disable-next-line no-unused-vars -- reserved for onboarding flow
  const _onboardingJustCompletedRef = useRef(false); // Track if onboarding just completed (prevent immediate legal check)

  // Get user ID from MSAL accounts - use refs to track stable values
  const accountsRef = useRef([]);
  const userIdRef = useRef(null);
  const userEmailRef = useRef(null);
  
  // Update refs only when accounts actually change (by comparing account IDs)
  useEffect(() => {
    // PUBLIC SITE: No MSAL instance - always set empty accounts
    accountsRef.current = [];
    userIdRef.current = null;
    userEmailRef.current = null;
    
    const currentAccounts = [];
    const currentAccountId = currentAccounts.length > 0 
      ? (currentAccounts[0].idTokenClaims?.sub || currentAccounts[0].idTokenClaims?.oid || currentAccounts[0].username)
      : null;
    const currentUserId = accountsRef.current.length > 0
      ? (accountsRef.current[0].idTokenClaims?.sub || accountsRef.current[0].idTokenClaims?.oid || accountsRef.current[0].username)
      : null;
    
    // Only update if account ID actually changed
    if (currentAccountId !== currentUserId) {
      accountsRef.current = currentAccounts;
      userIdRef.current = currentAccountId;
      userEmailRef.current = currentAccounts.length > 0 
        ? (currentAccounts[0].idTokenClaims?.email || currentAccounts[0].username)
        : null;
      
      // Reset profile loaded flag when user changes
      profileLoadedRef.current = false;
      loadingProfileRef.current = false;
    }
  }, []);

  // (onboarding sync removed — onboarding only exists in platform-auth)

  // Do NOT auto-redirect b-esper.com to auth portal on load. Let users browse the public site; they go to
  // portal.* only when they click "Sign in" (handled by PublicHeader / sign-in button using CUSTOMER_DOMAIN or REACT_APP_AUTH_PORTAL_URL).

  // Check if region/provider selection is needed - must happen BEFORE login
  // This runs after initial synchronous check to handle null availability
  // CRITICAL: Port 4001 is the public site - NEVER show RegionProviderModal there
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    
    const hostname = window.location.hostname;
    const port = window.location.port;
    const pathname = location.pathname;
    
    // CRITICAL: Explicitly prevent modal on port 4001 (public site)
    // Port 4001 should NEVER show RegionProviderModal
    if (port === '4001' || parseInt(port, 10) === 4001) {
      setShowRegionProviderModal(false);
      return;
    }
    
    const isOnAuthPortal = isAuthPortal(hostname);
    const selectedRegion = typeof localStorage !== 'undefined' ? localStorage.getItem('selectedRegion') : null;
    const selectedProvider = typeof localStorage !== 'undefined' ? localStorage.getItem('selectedProvider') : null;
    
    // CRITICAL: If user is on a protected route (like /bot-management), they must be authenticated
    // Don't show modal for authenticated users on protected routes
    const isProtectedRoute = pathname.includes('/bot-management') || 
                            pathname.includes('/dashboard') || 
                            pathname.includes('/profile') ||
                            pathname.includes('/settings');
    
    if (isProtectedRoute) {
      // User is on a protected route - they must be authenticated (via JWT)
      // Don't show modal
      logger.debug('APP', 'User on protected route - not showing region/provider modal', { pathname });
      setShowRegionProviderModal(false);
      return;
    }
    
    // Debug logging
    logger.debug('APP', 'Region/Provider Modal Check (useEffect)', {
      hostname,
      port,
      isOnAuthPortal,
      url: window.location.href,
      selectedRegion,
      selectedProvider,
      pathname,
      isProtectedRoute
    });
    
    // If not on auth portal (port 4002), don't show modal
    if (!isOnAuthPortal) {
      setShowRegionProviderModal(false);
      return;
    }
    
    // If region/provider is already selected, don't show modal
    if (selectedRegion && selectedProvider) {
      logger.debug('APP', 'Region/provider already selected - not showing modal', { selectedRegion, selectedProvider });
      setShowRegionProviderModal(false);
      return;
    }
    
    // On auth portal and region/provider not selected - show modal
    logger.info('APP', 'Showing region/provider modal (region/provider not selected)', { 
      isOnAuthPortal, 
      selectedRegion, 
      selectedProvider,
      hostname,
      port: window.location.port
    });
    setShowRegionProviderModal(true);
  }, [location.pathname, location.search]);

  // Check if user type selection is needed - must happen BEFORE onboarding
  useEffect(() => {
    if (!null || userTypeLoading) {
      return;
    }

    const accounts = null;
    if (accounts.length === 0) {
      setShowUserTypeSelection(false);
      return;
    }

    // Check if user has completed the selection
    const selectionCompleted = localStorage.getItem('userTypeSelectionCompleted');
    
    // Show modal if:
    // 1. User is authenticated
    // 2. Selection hasn't been completed
    // Don't show onboarding if user type selection is needed
    if (!selectionCompleted) {
      setShowUserTypeSelection(true);
      setShowOnboarding(false); // Prevent onboarding from showing until selection is made
    } else {
      setShowUserTypeSelection(false);
    }
  }, [ userTypeLoading]);

  // Load user profile to check intro field
  useEffect(() => {
    // Prevent multiple simultaneous loads
    if (loadingProfileRef.current) {
      return;
    }

    // Prevent loading if we already have a profile for this user
    if (profileLoadedRef.current) {
      return;
    }

    // Get current values from refs (they may have changed)
    const currentUserEmail = userEmailRef.current;
    const currentAccounts = accountsRef.current;

    // Early return if no null or user email
    if (!null || !currentUserEmail) {
      return;
    }

    // Check accounts safely
    if (currentAccounts.length === 0) {
      return;
    }

    const loadUserProfile = async () => {
      loadingProfileRef.current = true;
      
      try {
        const apiClient = initializeApiClient(null);
        // Get user profile - this endpoint returns {success: true, data: {...user data...}}
        const profileResponse = await apiClient.getUserProfile().catch((error) => {
          // Check if this is a re-authentication error
          // Add cooldown to prevent multiple modals from showing at once
          const now = Date.now();
          const cooldownPeriod = 5000; // 5 seconds cooldown
          
          if ((error?.message === 'REAUTH_REQUIRED' || error?.name === 'InteractionRequiredAuthError') &&
              (now - reauthErrorCooldownRef.current) > cooldownPeriod) {
            reauthErrorCooldownRef.current = now;
            setReauthError(error);
          }
          return null;
        });
        
        if (profileResponse && profileResponse.success) {
          // The response structure is {success: true, data: {...user data...}}
          const user = profileResponse.data;
          
          if (user) {
            setUserProfile(user);
            profileLoadedRef.current = true;
            
            // CRITICAL: Check legal_agreements directly from profile response (normalize: backend may use short keys)
            const legalAgreements = normalizeLegalAgreementsFromApi(user.legal_agreements);
            if (legalAgreements && typeof legalAgreements === 'object') {
              const requiredAgreements = [
                'terms_of_service',
                'data_processing_agreement',
                'privacy_policy',
                'end_user_license_agreement',
                'acceptable_use'
              ];
              
              const allAccepted = requiredAgreements.every(key => {
                const agreement = legalAgreements[key];
                // Handle both object format {agreed: true} and legacy boolean format
                // Also check acceptable_use_policy as fallback (backend does this)
                if (key === 'acceptable_use') {
                  const value = agreement || legalAgreements['acceptable_use_policy'];
                  if (value && typeof value === 'object') {
                    return value.agreed === true;
                  } else if (typeof value === 'boolean') {
                    return value === true;
                  }
                  return false;
                } else {
                  if (agreement && typeof agreement === 'object') {
                    return agreement.agreed === true;
                  } else if (typeof agreement === 'boolean') {
                    return agreement === true;
                  }
                  return false;
                }
              });
              
              logger.info('APP', '🔍 EARLY CHECK: Legal agreements from profile in loadUserProfile', {
                allAccepted,
                hasLegalAgreements: !!legalAgreements,
                agreementKeys: Object.keys(legalAgreements),
                agreementDetails: requiredAgreements.map(key => ({
                  key,
                  agreed: legalAgreements[key]?.agreed === true,
                  hasAgreement: !!legalAgreements[key],
                  agreementValue: legalAgreements[key]
                }))
              });
              
              // CRITICAL: If ALL accepted, IMMEDIATELY hide modal and STOP
              if (allAccepted) {
                logger.info('APP', '✅✅✅ ALL 5 AGREEMENTS ACCEPTED IN PROFILE - FORCING MODAL TO FALSE IMMEDIATELY');
                setShowForcedLegalModal(false);
                // Don't let any other code show the modal
                return; // Exit early - don't continue with onboarding check
              } else {
                logger.info('APP', '❌ Missing agreements in profile - modal may show after company data check', {
                  missing: requiredAgreements.filter(key => {
                    const agreement = legalAgreements[key];
                    if (key === 'acceptable_use') {
                      const value = agreement || legalAgreements['acceptable_use_policy'];
                      return !(value && ((typeof value === 'object' && value.agreed === true) || (typeof value === 'boolean' && value === true)));
                    }
                    return !(agreement && ((typeof agreement === 'object' && agreement.agreed === true) || (typeof agreement === 'boolean' && agreement === true)));
                  })
                });
              }
            } else {
              logger.warn('APP', 'No legal_agreements object in profile response');
            }
            
            // Check if intro field is true (defaults to true for new users)
            // If intro is undefined, default to true to show intro
            // BUT only show onboarding if user type selection is already completed
            const showIntro = user.intro !== false; // Default to true if undefined
            const selectionCompleted = localStorage.getItem('userTypeSelectionCompleted');
            
            // Only show onboarding if:
            // 1. User wants to see intro (intro !== false)
            // 2. User has already selected their type
            if (showIntro && selectionCompleted) {
              setShowOnboarding(true);
            }
          }
        }
      } catch (error) {
        logger.error('APP', 'Error loading user profile', { error: error.message });
      } finally {
        loadingProfileRef.current = false;
      }
    };
    
    loadUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once; refs used inside

  // Store null in ref to prevent callback recreation
  const instanceRef = useRef(null);
  
  useEffect(() => {
    instanceRef.current = null;
  }, []);

  // (onboarding intro overlay removed — onboarding only exists in platform-auth)

  // CRITICAL: Persistent legal compliance check - ALWAYS check on every page load and route change
  // This ensures users cannot bypass legal agreements by reloading or navigating
  // BUT: For new users, show onboarding (with company info) FIRST, then legal agreements
  useEffect(() => {
    const checkLegalCompliance = async () => {
      // CRITICAL: FIRST check if we already have profile data with all agreements accepted (normalize backend short keys)
      const normalizedProfileLegal = userProfile?.legal_agreements ? normalizeLegalAgreementsFromApi(userProfile.legal_agreements) : null;
      if (userProfile && normalizedProfileLegal && typeof normalizedProfileLegal === 'object') {
        const requiredAgreements = [
          'terms_of_service',
          'data_processing_agreement',
          'privacy_policy',
          'end_user_license_agreement',
          'acceptable_use'
        ];
        
        const allAccepted = requiredAgreements.every(key => {
          const agreement = normalizedProfileLegal[key];
          if (key === 'acceptable_use') {
            const value = agreement || normalizedProfileLegal['acceptable_use'];
            return value && ((typeof value === 'object' && value.agreed === true) || (typeof value === 'boolean' && value === true));
          }
          return agreement && ((typeof agreement === 'object' && agreement.agreed === true) || (typeof agreement === 'boolean' && agreement === true));
        });
        
        if (allAccepted) {
          logger.info('APP', '🚫 BLOCKING MODAL: All agreements already accepted in userProfile - hiding modal immediately');
          setShowForcedLegalModal(false);
          complianceCheckInProgressRef.current = false;
          return; // EXIT - don't do any other checks
        }
      }
      
      // Skip check if user is not authenticated
      if (false) { // PUBLIC SITE: No MSAL instance
        setShowForcedLegalModal(false); // Hide modal if not authenticated
        return;
      }

      const accounts = null;
      if (!accounts || accounts.length === 0) {
        setShowForcedLegalModal(false); // Hide modal if no accounts
        return;
      }

      // Skip if check is already in progress (prevent concurrent checks)
      if (complianceCheckInProgressRef.current) {
        return;
      }

      // Skip if we've already checked this pathname recently (prevent duplicate checks on same route)
      const currentPathname = location.pathname;
      if (lastCheckedPathnameRef.current === currentPathname && legalComplianceCheckRef.current) {
        return;
      }

      // CRITICAL: Don't show forced modal if user is on a legal page - they need to read the agreements first
      const isLegalPagePath = isLegalPage(currentPathname);
      if (isLegalPagePath) {
        logger.debug('APP', 'User is on legal page - hiding forced modal to allow reading agreements');
        setShowForcedLegalModal(false);
        complianceCheckInProgressRef.current = false;
        return; // Don't show modal on legal pages - user needs to read first
      }

      // NOTE: Onboarding is handled by platform-auth only (no OnboardingOverlay in platform-public)

      try {
        complianceCheckInProgressRef.current = true;
        legalComplianceCheckRef.current = true;
        lastCheckedPathnameRef.current = currentPathname;
        
        // ============================================================================
        // STEP 1: CHECK COMPANY DATA FIRST (SYNCHRONOUS - NO API CALLS)
        // ============================================================================
        // CRITICAL: This MUST happen BEFORE any legal compliance check
        // Check localStorage FIRST (instant, no async)
        logger.debug('APP', 'STEP 1: Checking company data (localStorage)');
        const onboardingCompanyData = localStorage.getItem('onboarding_company_data');
        let hasValidCompanyData = false;
        
        if (onboardingCompanyData && onboardingCompanyData !== 'null' && onboardingCompanyData !== '{}' && onboardingCompanyData.trim() !== '') {
          try {
            const parsed = JSON.parse(onboardingCompanyData);
            // Check if it has ALL required fields (name, country, street_address, postal_code, city)
            hasValidCompanyData = !!(parsed && parsed.name && parsed.country && parsed.street_address && parsed.postal_code && parsed.city);
            logger.debug('APP', 'Company data validation', {
              hasValidData: hasValidCompanyData,
              hasName: !!parsed.name,
              hasCountry: !!parsed.country,
              hasStreet: !!parsed.street_address,
              hasPostal: !!parsed.postal_code,
              hasCity: !!parsed.city
            });
          } catch (e) {
            logger.error('APP', 'Error parsing company data', { error: e.message });
            // Invalid JSON, treat as no data
            hasValidCompanyData = false;
          }
        } else {
          logger.debug('APP', 'No company data in localStorage or data is empty/null');
        }
        
        // If NO valid company data in localStorage, check backend before showing onboarding
        // This prevents showing onboarding again if localStorage was cleared but backend has data
        if (!hasValidCompanyData) {
          logger.info('APP', 'No valid company data in localStorage, checking backend');
          
          // Before showing onboarding, double-check backend (might be faster than full profile check)
          try {
            const apiClient = initializeApiClient(null);
            const profileResponse = await apiClient.apiRequest('/v1/users/profile?include_tenant_company=1', {
              method: 'GET'
            });
            
            if (profileResponse && profileResponse.success && profileResponse.data) {
              const profileData = profileResponse.data;
              const backendCompanyData = profileData.onboarding_company_data;
              const onboardingCompleted = profileData.onboarding_completed === true || profileData.intro === false ||
                (profileData.onboarding && profileData.onboarding.completed === true);

              if (onboardingCompleted) {
                logger.info('APP', 'Onboarding marked as completed in profile - not showing onboarding again');
                // Sync localStorage if backend has data
                if (backendCompanyData && typeof backendCompanyData === 'object') {
                  try {
                    localStorage.setItem('onboarding_company_data', JSON.stringify(backendCompanyData));
                    logger.debug('APP', 'Synced company data from backend to localStorage');
                  } catch (e) {
                    logger.warn('APP', 'Could not sync company data to localStorage', { error: e.message });
                  }
                }
                complianceCheckInProgressRef.current = false;
                return; // Don't show onboarding if it's already been completed
              }
              
              // Check if backend has valid company data (only require name and country - other fields are optional)
              if (backendCompanyData && typeof backendCompanyData === 'object' &&
                  backendCompanyData.name && backendCompanyData.country) {
                logger.info('APP', 'Found valid company data in backend - syncing to localStorage');
                // Sync to localStorage
                try {
                  localStorage.setItem('onboarding_company_data', JSON.stringify(backendCompanyData));
                  hasValidCompanyData = true; // Update flag so we continue with normal flow
                } catch (e) {
                  logger.warn('APP', 'Could not sync company data to localStorage', { error: e.message });
                }
              }
            }
          } catch (backendCheckErr) {
            logger.warn('APP', 'Could not check backend for company data', { error: backendCheckErr?.message });
          }
          
          // If still no valid data after backend check, show onboarding
          if (!hasValidCompanyData) {
            logger.info('APP', 'NEW USER - No company data found in localStorage or backend. FORCING onboarding FIRST, BLOCKING legal check');
            setShowForcedLegalModal(false); // CRITICAL: Hide legal modal
            setShowOnboarding(true); // CRITICAL: Show onboarding
            complianceCheckInProgressRef.current = false;
            return; // STOP - don't check legal compliance until onboarding completes
          }
        }
        
        // ============================================================================
        // STEP 2: If localStorage has data, check user profile (async)
        // ============================================================================
        logger.debug('APP', 'STEP 2: Checking user profile for company data');
        let userHasValidCompanyData = false;
        try {
          const apiClient = initializeApiClient(null);
          const profileResponse = await apiClient.apiRequest('/v1/users/profile?include_tenant_company=1', {
            method: 'GET'
          });
          if (profileResponse && profileResponse.success && profileResponse.data) {
            const companyData = profileResponse.data.onboarding_company_data;
            if (companyData && typeof companyData === 'object') {
              // Validate it has minimum required fields (name and country - other fields are optional)
              userHasValidCompanyData = !!(companyData.name && companyData.country);
              logger.debug('APP', 'Profile company data validation', {
                hasValidData: userHasValidCompanyData,
                hasName: !!companyData.name,
                hasCountry: !!companyData.country,
                hasStreet: !!companyData.street_address,
                hasPostal: !!companyData.postal_code,
                hasCity: !!companyData.city
              });
            }
          }
        } catch (e) {
          logger.warn('APP', 'Could not check user profile for company data', { error: e.message });
          // If profile check fails, assume no data (show onboarding)
          userHasValidCompanyData = false;
        }
        
        // If user profile doesn't have valid data either, show onboarding
        if (!userHasValidCompanyData) {
          logger.info('APP', 'NEW USER - Profile has no company data. FORCING onboarding FIRST, BLOCKING legal check');
          setShowForcedLegalModal(false); // CRITICAL: Hide legal modal
          setShowOnboarding(true); // CRITICAL: Show onboarding
          complianceCheckInProgressRef.current = false;
          return; // STOP - don't check legal compliance until onboarding completes
        }
        
        // ============================================================================
        // STEP 3: User has valid company data - NOW check legal compliance from profile
        // ============================================================================
        logger.info('APP', 'User has company data - checking legal agreements from profile');
        
        // CRITICAL: Double-check onboarding is not active before showing legal modal
        if (showOnboarding) {
          logger.debug('APP', 'Onboarding is active - blocking legal agreements modal');
          setShowForcedLegalModal(false);
          complianceCheckInProgressRef.current = false;
          return;
        }
        
        // CRITICAL: Check legal agreements directly from profile API response
        // This is the source of truth - don't use cached compliance checks
        try {
          const apiClient = initializeApiClient(null);
          const profileResponse = await apiClient.getUserProfile();
          
          if (profileResponse && profileResponse.success && profileResponse.data) {
            const user = profileResponse.data;
            const legalAgreements = normalizeLegalAgreementsFromApi(user.legal_agreements);
            const requiredAgreements = [
              'terms_of_service',
              'data_processing_agreement',
              'privacy_policy',
              'end_user_license_agreement',
              'acceptable_use'
            ];

            let allAccepted = false;
            if (legalAgreements && typeof legalAgreements === 'object') {
              allAccepted = requiredAgreements.every(key => {
                const agreement = legalAgreements[key];
                if (key === 'acceptable_use') {
                  const value = agreement || legalAgreements['acceptable_use_policy'];
                  if (value && typeof value === 'object') return value.agreed === true;
                  if (typeof value === 'boolean') return value === true;
                  return false;
                }
                if (agreement && typeof agreement === 'object') return agreement.agreed === true;
                if (typeof agreement === 'boolean') return agreement === true;
                return false;
              });

              const agreementDetails = requiredAgreements.map(key => {
                const agreement = legalAgreements[key];
                const agreed = agreement && typeof agreement === 'object'
                  ? agreement.agreed === true
                  : (typeof agreement === 'boolean' ? agreement === true : false);
                return { key, agreed };
              });

              logger.info('APP', 'Legal agreements check from profile', { allAccepted, agreementDetails });
            }

            if (allAccepted) {
              logger.info('APP', 'All legal agreements accepted - hiding modal');
              setShowForcedLegalModal(false);
              complianceCheckInProgressRef.current = false;
              return;
            }

            // Not all accepted — show forced modal
            if (legalAgreements && typeof legalAgreements === 'object') {
              const missing = requiredAgreements.filter(key => {
                const agreement = legalAgreements[key];
                if (agreement && typeof agreement === 'object') return agreement.agreed !== true;
                if (typeof agreement === 'boolean') return agreement !== true;
                return true;
              });
              logger.info('APP', 'Missing legal agreements - showing forced modal', { missing });
            } else {
              logger.warn('APP', 'No legal_agreements in profile - showing modal');
            }
            const isLegalPagePath = isLegalPage(currentPathname);
            if (!isLegalPagePath) {
              setShowForcedLegalModal(true);
            } else {
              setShowForcedLegalModal(false);
            }
          } else {
            logger.warn('APP', 'Profile response invalid - hiding modal to avoid blocking');
            setShowForcedLegalModal(false);
          }
        } catch (error) {
          logger.error('APP', 'Error checking legal agreements from profile', { error: error.message });
          // On error, don't block user - hide modal
          setShowForcedLegalModal(false);
        }
      } catch (error) {
        logger.error('APP', 'Error checking legal compliance', { error: error.message });
        // On error, DON'T show legal modal if onboarding should be shown
        // Check company data first even on error
        const onboardingCompanyData = localStorage.getItem('onboarding_company_data');
        const hasData = onboardingCompanyData && onboardingCompanyData !== 'null' && onboardingCompanyData !== '{}' && onboardingCompanyData.trim() !== '';
        let hasValidData = false;
        if (hasData) {
          try {
            const parsed = JSON.parse(onboardingCompanyData);
            // Only require name and country - other fields are optional
            hasValidData = !!(parsed && parsed.name && parsed.country);
          } catch (e) {
            hasValidData = false;
          }
        }
        
        // Only show legal modal on error if user has valid company data
        // Otherwise, show onboarding
        if (accounts && accounts.length > 0) {
          if (hasValidData) {
            logger.warn('APP', 'Compliance check failed but user has company data - showing modal as safety measure');
            setShowForcedLegalModal(true);
          } else {
            logger.warn('APP', 'Compliance check failed and NO company data - showing onboarding instead');
            setShowForcedLegalModal(false);
            setShowOnboarding(true);
          }
        }
      } finally {
        complianceCheckInProgressRef.current = false;
        // Reset check flag after a delay to allow re-checking on navigation
        setTimeout(() => {
          legalComplianceCheckRef.current = false;
        }, 2000); // Increased delay to prevent rapid re-checks
      }
    };

    // Check immediately when pathname is available or location changes
    checkLegalCompliance();

    // Also listen for legal agreements updates to re-check immediately
    const handleLegalAgreementsUpdated = async () => {
      logger.info('APP', 'Legal agreements updated - clearing cache and re-checking compliance');
      const accounts = null;
      if (accounts && accounts.length > 0) {
        const userId = accounts[0]?.homeAccountId || accounts[0]?.localAccountId || accounts[0]?.username;
        if (userId) {
          try {
            const { clearComplianceCache, clearCachedApiResponse } = await import('./utils/legalAgreementsGuard');
            clearComplianceCache(userId);
            clearCachedApiResponse(userId);
            logger.debug('APP', 'Cleared all legal agreements caches for user');
          } catch (e) {
            logger.warn('APP', 'Could not clear cache', { error: e.message });
          }
        }
      }
      legalComplianceCheckRef.current = false; // Allow immediate re-check
      lastCheckedPathnameRef.current = null; // Reset pathname to force re-check
    };

    window.addEventListener('legalAgreementsUpdated', handleLegalAgreementsUpdated);
    
    // Listen for onboarding completion (company info collected) - then check legal compliance
    const handleOnboardingCompanyInfoCompleted = () => {
      logger.info('APP', 'Company info collected in onboarding - now checking legal compliance');
      legalComplianceCheckRef.current = false; // Allow immediate re-check
      lastCheckedPathnameRef.current = null; // Reset pathname to force re-check
      complianceCheckInProgressRef.current = false; // Reset progress flag
    };
    
    window.addEventListener('onboardingCompanyInfoCompleted', handleOnboardingCompanyInfoCompleted);

    return () => {
      window.removeEventListener('legalAgreementsUpdated', handleLegalAgreementsUpdated);
      window.removeEventListener('onboardingCompanyInfoCompleted', handleOnboardingCompanyInfoCompleted);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run on pathname only
  }, [location.pathname]);

  // Handle completion of forced legal modal (kept for future use when modal is re-enabled)
  // eslint-disable-next-line no-unused-vars -- reserved for forced legal modal
  const _handleForcedLegalModalComplete = useCallback(async () => {
    logger.info('APP', 'All legal agreements accepted - closing forced modal');
    setShowForcedLegalModal(false);
    legalComplianceCheckRef.current = false; // Allow re-check
    
    // Clear compliance cache to ensure fresh check
    try {
      const { clearComplianceCache } = await import('utils/legalAgreementsGuard');
      clearComplianceCache();
      logger.info('APP', 'Cleared compliance cache after modal completion');
    } catch (err) {
      logger.warn('APP', 'Failed to clear compliance cache', { error: err.message });
    }
    
    // PUBLIC SITE: No profile check needed - always unauthenticated
    // Removed all profile/onboarding checks for public site
  }, []);
  
  // (handleOnboardingComplete removed — onboarding only exists in platform-auth)

  const handleUserTypeSelection = (selectedViewMode) => {
    setShowUserTypeSelection(false);
    // The modal already updates viewMode and localStorage
  };

  // Check for demo bot activation after authentication
  useEffect(() => {
    const checkDemoBotActivation = async () => {
      if (false) { // PUBLIC SITE: No MSAL instance
        return;
      }
      
      const accounts = null;
      if (accounts.length > 0) {
        // Check if there are any non-activated demo bots in localStorage
        const needsActivation = localStorage.getItem('needsActivation');
        const demoBots = JSON.parse(localStorage.getItem('demoBots') || '[]');
        const nonActivatedBots = demoBots.filter(bot => !bot.activated);
        
        if (needsActivation === 'true' && nonActivatedBots.length > 0) {
          try {
            // Initialize API client first
            await initializeApiClient(null);
            
            // Activate each demo bot
            for (const demoBot of nonActivatedBots) {
              try {
                // Use centralized active account helper
                const { getOrSelectActiveAccount } = await import('./utils/msalConfig');
                const account = getOrSelectActiveAccount(null);
                if (!account) {
                  logger.warn('APP', 'No active account for demo bot activation');
                  continue;
                }
                const { getApiEndpoint } = await import('utils/centralizedConfig');
                const apiBase = await getApiEndpoint('public');
                const activateUrl = apiBase ? (apiBase.endsWith('/api') ? `${apiBase}/chat/activate` : `${apiBase}/api/chat/activate`) : '/api/chat/activate';
                const activationResponse = await fetch(activateUrl, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${account.idToken}`
                  },
                  body: JSON.stringify({
                    bot_id: demoBot.botId,
                    management_id: demoBot.managementId,
                    management_secret: demoBot.managementSecret
                  })
                });
                
                if (activationResponse.ok) {
                  const result = await activationResponse.json();
                  
                  // Mark bot as activated
                  demoBot.activated = true;
                  demoBot.activationDate = new Date().toISOString();
                  demoBot.tenantId = result.tenant_id;
                  
                  // Show success notification
                  showActivationSuccess(demoBot, result);
                } else {
                  logger.error('APP', `Failed to activate demo bot ${demoBot.botId}`, { status: activationResponse.statusText });
                }
                
              } catch (error) {
                logger.error('APP', `Error activating demo bot ${demoBot.botId}`, { error: error.message });
              }
            }
            
            // Update localStorage with activated bots
            localStorage.setItem('demoBots', JSON.stringify(demoBots));
            localStorage.removeItem('needsActivation');
            
          } catch (error) {
            logger.error('APP', 'Error during demo bot activation process', { error: error.message });
          }
        }
      }
    };

    // Initialize permissions and API client when user is authenticated
    // BEST PRACTICE: Don't probe for tokens at startup - only request tokens when actually needed for API calls
    // This prevents AADSTS160021 loops from unnecessary silent token requests
    const accounts = null || [];
    if (accounts.length > 0) {
      // Initialize API client
      initializeApiClient(null);
      
      // Initialize permissions based on account presence (no token probe needed)
      // Permissions will be initialized using account claims, and API calls will request tokens when needed
      const initPermissionsWhenReady = async () => {
        try {
          // Use centralized active account helper
          const { getOrSelectActiveAccount } = await import('./utils/msalConfig');
          const account = getOrSelectActiveAccount(null);
          
          if (!account) {
            logger.warn('APP', 'No active account available for permissions initialization');
            return;
          }
          
          // BEST PRACTICE: Initialize permissions using account claims (no token request needed)
          // Token requests should only happen when making actual API calls that require access tokens
          // This prevents AADSTS160021 loops from unnecessary silent token probes
          logger.debug('APP', 'Initializing permissions using account claims (no token probe)');
          await initializePermissions(null);
        } catch (error) {
          // If permissions initialization fails, it will fall back to basic_user
          // This is not critical - permissions can be initialized later when API calls are made
          logger.warn('APP', 'Permissions initialization failed (will retry on first API call)', { error: error.message });
        }
      };
      
      initPermissionsWhenReady();
      
      // Check for demo bot activation
      checkDemoBotActivation();
    }

    // Bot initialization is handled by BotWidget component
    // No need for duplicate initialization here
  }, []);

  const showActivationSuccess = (bot, result) => {
    // Create and show a success notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
      color: white;
      padding: 16px 20px;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(40, 167, 69, 0.3);
      z-index: 10000;
      max-width: 400px;
      animation: slideInRight 0.5s ease;
    `;
    
    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 12px;">
        <div style="font-size: 24px;">🎉</div>
        <div>
          <h4 style="margin: 0 0 4px 0; font-size: 16px; font-weight: 600;">Demo Bot Activated!</h4>
          <p style="margin: 0; font-size: 14px; opacity: 0.9;">
            Your bot <strong>${bot.botId}</strong> is now fully functional!
          </p>
        </div>
        <button onclick="this.parentElement.parentElement.remove()" style="
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 16px;
          font-weight: bold;
        ">×</button>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 10000);
  };

  // Get initial region/provider from domain or URL params
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const domainInfo = hostname ? parseDomain(hostname) : null;
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  
  let initialProvider = urlParams.get('provider') || (typeof window !== 'undefined' ? localStorage.getItem('selectedProvider') : null) || 'az';
  let initialRegion = urlParams.get('region') || (typeof window !== 'undefined' ? localStorage.getItem('selectedRegion') : null) || 'germanywestcentral';
  
  // Auto-detect from domain if available
  if (domainInfo) {
    const regionName = mapRegionCodeToName(domainInfo.region);
    if (regionName) {
      initialRegion = regionName;
    }
    if (domainInfo.provider) {
      initialProvider = domainInfo.provider;
    }
  }

  const handleRegionProviderSelect = (selection) => {
    setShowRegionProviderModal(false);
    // Store selection
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('selectedProvider', selection.provider);
      localStorage.setItem('selectedRegion', selection.region);
    }
    
    // Update URL with selection
    if (typeof window !== 'undefined') {
      const newParams = new URLSearchParams(window.location.search);
      newParams.set('region', selection.region);
      newParams.set('provider', selection.provider);
      window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
    }
  };

  if (!appContentReady) {
    if (process.env.NODE_ENV === 'development' || (typeof window !== 'undefined' && window.location && window.location.search && window.location.search.includes('ppdebug'))) {
      console.log('[PP-DBG] 6 AppContent returning null (gate closed, appContentReady=false)');
    }
    return null;
  }
  if (process.env.NODE_ENV === 'development' || (typeof window !== 'undefined' && window.location && window.location.search && window.location.search.includes('ppdebug'))) {
    console.log('[PP-DBG] 7 AppContent gate open, rendering providers and content');
  }

  // Block all navigation if region/provider modal is showing
  if (showRegionProviderModal) {
    return (
      <PublicTenantProvider>
        <ProductProvider>
          <I18nProvider>
            <div className="app">
              <RegionProviderModal
                onSelect={handleRegionProviderSelect}
                initialProvider={initialProvider}
                initialRegion={initialRegion}
              />
            </div>
          </I18nProvider>
        </ProductProvider>
      </PublicTenantProvider>
    );
  }

  return (
    <PublicTenantProvider>
      <ProductProvider>
        <I18nProvider>
          <div className="app">
      {/* Re-authentication UI - shown when token acquisition fails */}
      {reauthError && (
        <ReauthRequired 
          error={reauthError} 
          onDismiss={() => setReauthError(null)}
        />
      )}
      <div className="bsp-wavy-background">
        <canvas id="wavyCanvas"></canvas>
      </div>
      {/* PUBLIC SITE: No AuthConfigNotice - this is an unauthenticated public site */}
      {!isConfidentialPage && (
        <Header 
          sidebarOpen={sidebarOpen} 
          setSidebarOpen={setSidebarOpen}
          showSidebarToggle={false}
          onSignIn={handleSignIn}
        />
      )}
      <main className="main-content">
        <Routes>
          {/* Public routes with language support - No authentication required */}
          {/* Note: Auth portal routes are protected by modal check above */}
          <Route 
            path="/" 
            element={
              <PageGuards>
                <HomePage />
              </PageGuards>
            } 
          />
          <Route 
            path="/:lang/" 
            element={
              <PageGuards>
                <HomePage />
              </PageGuards>
            } 
          />
          {/* Auth callback routes - moved to platform-auth */}
          {/* <Route path="/auth/callback" element={<AuthCallback />} /> */}
          {/* <Route path="/:lang/auth/callback" element={<AuthCallback />} /> */}
          {/* Unprotected auth recovery routes - moved to platform-auth */}
          {/* <Route path="/auth/reset" element={<AuthReset />} /> */}
          {/* <Route path="/:lang/auth/reset" element={<AuthReset />} /> */}
          {/* <Route path="/logged-out" element={<LoggedOut />} /> */}
          {/* <Route path="/:lang/logged-out" element={<LoggedOut />} /> */}
          
          {/* ============================================================================
              AUTHENTICATED PAGES REMOVED - These routes moved to platform-auth
              ============================================================================
              Resource Management, hosted application management, and all authenticated resource pages
              should only exist in platform-auth, not platform-public.
              ============================================================================ */}
          
          {/* Dedicated Resource Type Management Pages - moved to platform-auth */}
          {/* <Route 
            path="/bot-management" 
            element={
                <PageGuards>
                  <BotManagement />
                </PageGuards>
              } />
          <Route 
            path="/:lang/bot-management" 
            element={
                <PageGuards>
                  <BotManagement />
                </PageGuards>
              } /> */}
          {/* Product management routes - moved to platform-auth */}
          {/* <Route 
            path="/resale-management" 
            element={...}
          />
          ... (all product management routes commented out) */}
          
          {/* Product pages */}
          <Route path="/roadmap" element={<PublicPageRedirect><Roadmap /></PublicPageRedirect>} />
          <Route path="/:lang/roadmap" element={<PublicPageRedirect><Roadmap /></PublicPageRedirect>} />
          
          {/* Static pages - Available to all users - Redirect to unauth portal if on auth portal */}
          <Route path="/about" element={<PublicPageRedirect><AboutUs /></PublicPageRedirect>} />
          <Route path="/:lang/about" element={<PublicPageRedirect><AboutUs /></PublicPageRedirect>} />
          <Route path="/docs" element={<TechnicalDocumentation />} />
          <Route path="/docs/*" element={<TechnicalDocumentation />} />
          <Route path="/:lang/docs" element={<TechnicalDocumentation />} />
          <Route path="/:lang/docs/*" element={<TechnicalDocumentation />} />
          <Route path="/contact" element={<AuthOnlyRedirect />} />
          <Route path="/:lang/contact" element={<AuthOnlyRedirect />} />
          <Route path="/terms" element={<PublicPageRedirect><LanguageRedirect><TermsOfService /></LanguageRedirect></PublicPageRedirect>} />
          <Route path="/:lang/terms" element={<PublicPageRedirect><TermsOfService /></PublicPageRedirect>} />
          <Route path="/privacy" element={<PublicPageRedirect><LanguageRedirect><PrivacyPolicy /></LanguageRedirect></PublicPageRedirect>} />
          <Route path="/:lang/privacy" element={<PublicPageRedirect><PrivacyPolicy /></PublicPageRedirect>} />
          <Route path="/impressum" element={<PublicPageRedirect><LanguageRedirect><Impressum /></LanguageRedirect></PublicPageRedirect>} />
          <Route path="/:lang/impressum" element={<PublicPageRedirect><Impressum /></PublicPageRedirect>} />
          <Route path="/changelog" element={<PublicPageRedirect><Changelog /></PublicPageRedirect>} />
          <Route path="/:lang/changelog" element={<PublicPageRedirect><Changelog /></PublicPageRedirect>} />
          <Route path="/help-center" element={<AuthOnlyRedirect />} />
          <Route path="/:lang/help-center" element={<AuthOnlyRedirect />} />
          <Route path="/community" element={<AuthOnlyRedirect />} />
          <Route path="/:lang/community" element={<AuthOnlyRedirect />} />
          <Route path="/become-partner" element={<AuthOnlyRedirect />} />
          <Route path="/:lang/become-partner" element={<AuthOnlyRedirect />} />
          <Route path="/feedback" element={<AuthOnlyRedirect />} />
          <Route path="/:lang/feedback" element={<AuthOnlyRedirect />} />
          <Route path="/cookie-settings" element={<PublicPageRedirect><LanguageRedirect><CookieSettings /></LanguageRedirect></PublicPageRedirect>} />
          <Route path="/:lang/cookie-settings" element={<PublicPageRedirect><CookieSettings /></PublicPageRedirect>} />
          <Route path="/gdpr" element={<PublicPageRedirect><LanguageRedirect><GDPR /></LanguageRedirect></PublicPageRedirect>} />
          <Route path="/:lang/gdpr" element={<PublicPageRedirect><GDPR /></PublicPageRedirect>} />
          <Route path="/dpa" element={<PublicPageRedirect><LanguageRedirect><DataProcessingAgreement /></LanguageRedirect></PublicPageRedirect>} />
          <Route path="/:lang/dpa" element={<PublicPageRedirect><DataProcessingAgreement /></PublicPageRedirect>} />
          <Route path="/press" element={<PublicPageRedirect><Press /></PublicPageRedirect>} />
          <Route path="/:lang/press" element={<PublicPageRedirect><Press /></PublicPageRedirect>} />
          <Route path="/strategy" element={<PublicPageRedirect><Strategy /></PublicPageRedirect>} />
          <Route path="/:lang/strategy" element={<PublicPageRedirect><Strategy /></PublicPageRedirect>} />
          <Route path="/besper-ai" element={<PublicPageRedirect><BesperAi /></PublicPageRedirect>} />
          <Route path="/:lang/besper-ai" element={<PublicPageRedirect><BesperAi /></PublicPageRedirect>} />
          <Route path="/investors" element={<PublicPageRedirect><Investors /></PublicPageRedirect>} />
          <Route path="/:lang/investors" element={<PublicPageRedirect><Investors /></PublicPageRedirect>} />
          <Route path="/newsletter" element={<PublicPageRedirect><Newsletter /></PublicPageRedirect>} />
          <Route path="/:lang/newsletter" element={<PublicPageRedirect><Newsletter /></PublicPageRedirect>} />
          <Route path="/events" element={<PublicPageRedirect><Events /></PublicPageRedirect>} />
          <Route path="/:lang/events" element={<PublicPageRedirect><Events /></PublicPageRedirect>} />
          <Route path="/architecture" element={<PublicPageRedirect><Architecture /></PublicPageRedirect>} />
          <Route path="/:lang/architecture" element={<PublicPageRedirect><Architecture /></PublicPageRedirect>} />
          <Route path="/architecture/runtime" element={<PublicPageRedirect><Runtime /></PublicPageRedirect>} />
          <Route path="/:lang/architecture/runtime" element={<PublicPageRedirect><Runtime /></PublicPageRedirect>} />
          <Route path="/architecture/gateway" element={<PublicPageRedirect><Gateway /></PublicPageRedirect>} />
          <Route path="/:lang/architecture/gateway" element={<PublicPageRedirect><Gateway /></PublicPageRedirect>} />
          <Route path="/architecture/compute" element={<PublicPageRedirect><Compute /></PublicPageRedirect>} />
          <Route path="/:lang/architecture/compute" element={<PublicPageRedirect><Compute /></PublicPageRedirect>} />
          <Route path="/architecture/database" element={<PublicPageRedirect><Database /></PublicPageRedirect>} />
          <Route path="/:lang/architecture/database" element={<PublicPageRedirect><Database /></PublicPageRedirect>} />
          <Route path="/architecture/components" element={<PublicPageRedirect><ArchitectureComponents /></PublicPageRedirect>} />
          <Route path="/:lang/architecture/components" element={<PublicPageRedirect><ArchitectureComponents /></PublicPageRedirect>} />
          <Route path="/architecture/processes" element={<PublicPageRedirect><Processes /></PublicPageRedirect>} />
          <Route path="/:lang/architecture/processes" element={<PublicPageRedirect><Processes /></PublicPageRedirect>} />
          <Route path="/architecture/processes/customer-portal" element={<PublicPageRedirect><CustomerPortalAPIs /></PublicPageRedirect>} />
          <Route path="/:lang/architecture/processes/customer-portal" element={<PublicPageRedirect><CustomerPortalAPIs /></PublicPageRedirect>} />
          <Route path="/architecture/processes/customer-portal/:groupId" element={<PublicPageRedirect><CustomerPortalApiGroup /></PublicPageRedirect>} />
          <Route path="/:lang/architecture/processes/customer-portal/:groupId" element={<PublicPageRedirect><CustomerPortalApiGroup /></PublicPageRedirect>} />
          <Route path="/architecture/processes/customer-portal/:groupId/:endpointId" element={<PublicPageRedirect><CustomerPortalEndpoint /></PublicPageRedirect>} />
          <Route path="/:lang/architecture/processes/customer-portal/:groupId/:endpointId" element={<PublicPageRedirect><CustomerPortalEndpoint /></PublicPageRedirect>} />
          <Route path="/architecture/processes/public" element={<PublicPageRedirect><PublicAPIs /></PublicPageRedirect>} />
          <Route path="/:lang/architecture/processes/public" element={<PublicPageRedirect><PublicAPIs /></PublicPageRedirect>} />
          <Route path="/architecture/processes/customer-portal/endpoints/resources" element={<PublicPageRedirect><ResourcesEndpoint /></PublicPageRedirect>} />
          <Route path="/:lang/architecture/processes/customer-portal/endpoints/resources" element={<PublicPageRedirect><ResourcesEndpoint /></PublicPageRedirect>} />
          <Route path="/architecture/processes/public/endpoints/bot-operations" element={<PublicPageRedirect><BotOperationsEndpoint /></PublicPageRedirect>} />
          <Route path="/:lang/architecture/processes/public/endpoints/bot-operations" element={<PublicPageRedirect><BotOperationsEndpoint /></PublicPageRedirect>} />
          <Route path="/partners" element={<PublicPageRedirect><Partners /></PublicPageRedirect>} />
          <Route path="/:lang/partners" element={<PublicPageRedirect><Partners /></PublicPageRedirect>} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/:lang/marketplace" element={<Marketplace />} />
          <Route path="/testimonials" element={<PublicPageRedirect><Testimonials /></PublicPageRedirect>} />
          <Route path="/:lang/testimonials" element={<PublicPageRedirect><Testimonials /></PublicPageRedirect>} />
          <Route path="/blog" element={<PublicPageRedirect><Blog /></PublicPageRedirect>} />
          <Route path="/:lang/blog" element={<PublicPageRedirect><Blog /></PublicPageRedirect>} />
          <Route path="/faq" element={<AuthOnlyRedirect />} />
          <Route path="/:lang/faq" element={<AuthOnlyRedirect />} />
          <Route path="/bug-reports" element={<AuthOnlyRedirect />} />
          <Route path="/:lang/bug-reports" element={<AuthOnlyRedirect />} />
          <Route path="/sla" element={<PublicPageRedirect><ServiceLevelAgreement /></PublicPageRedirect>} />
          <Route path="/:lang/sla" element={<PublicPageRedirect><ServiceLevelAgreement /></PublicPageRedirect>} />
          <Route path="/status" element={<PublicPageRedirect><Status /></PublicPageRedirect>} />
          <Route path="/:lang/status" element={<PublicPageRedirect><Status /></PublicPageRedirect>} />
          <Route path="/eula" element={<PublicPageRedirect><LanguageRedirect><EULA /></LanguageRedirect></PublicPageRedirect>} />
          <Route path="/:lang/eula" element={<PublicPageRedirect><EULA /></PublicPageRedirect>} />
          <Route path="/acceptable-use" element={<PublicPageRedirect><LanguageRedirect><AcceptableUse /></LanguageRedirect></PublicPageRedirect>} />
          <Route path="/:lang/acceptable-use" element={<PublicPageRedirect><AcceptableUse /></PublicPageRedirect>} />
          <Route path="/disclaimer" element={<PublicPageRedirect><LanguageRedirect><Disclaimer /></LanguageRedirect></PublicPageRedirect>} />
          <Route path="/:lang/disclaimer" element={<PublicPageRedirect><Disclaimer /></PublicPageRedirect>} />
          <Route path="/privacy-policy" element={<PublicPageRedirect><LanguageRedirect><PrivacyPolicy /></LanguageRedirect></PublicPageRedirect>} />
          <Route path="/:lang/privacy-policy" element={<PublicPageRedirect><PrivacyPolicy /></PublicPageRedirect>} />
          <Route path="/api-docs" element={<Navigate to="/docs/getting-started/quickstart" replace />} />
          <Route path="/:lang/api-docs" element={<Navigate to="/docs/getting-started/quickstart" replace />} />
          {/* Data Processing Agreement (DPA) - new legal route aliases */}
          <Route path="/legal/data-processing-agreement" element={<PublicPageRedirect><LanguageRedirect><DataProcessingAgreement /></LanguageRedirect></PublicPageRedirect>} />
          <Route path="/:lang/legal/data-processing-agreement" element={<PublicPageRedirect><DataProcessingAgreement /></PublicPageRedirect>} />
          
          {/* Business Data Processing Agreement - cost pool level */}
          <Route path="/business-dpa" element={<PublicPageRedirect><LanguageRedirect><BusinessDataProcessingAgreement /></LanguageRedirect></PublicPageRedirect>} />
          <Route path="/:lang/business-dpa" element={<PublicPageRedirect><BusinessDataProcessingAgreement /></PublicPageRedirect>} />
          
          {/* Styling Mock / Press Kit Routes */}
          <Route path="/styling-mock" element={<StylingMockIndex />} />
          <Route path="/:lang/styling-mock" element={<StylingMockIndex />} />
          <Route path="/styling-mock/buttons" element={<StylingMockButtons />} />
          <Route path="/:lang/styling-mock/buttons" element={<StylingMockButtons />} />
          <Route path="/styling-mock/containers" element={<StylingMockContainers />} />
          <Route path="/:lang/styling-mock/containers" element={<StylingMockContainers />} />
          <Route path="/styling-mock/ui" element={<StylingMockUI />} />
          <Route path="/:lang/styling-mock/ui" element={<StylingMockUI />} />
          <Route path="/styling-mock/typography" element={<StylingMockTypography />} />
          <Route path="/:lang/styling-mock/typography" element={<StylingMockTypography />} />
          <Route path="/styling-mock/logo" element={<StylingMockLogo />} />
          <Route path="/:lang/styling-mock/logo" element={<StylingMockLogo />} />
          <Route path="/styling-mock/layouts" element={<StylingMockLayouts />} />
          <Route path="/:lang/styling-mock/layouts" element={<StylingMockLayouts />} />
          <Route path="/styling-mock/animations" element={<StylingMockAnimations />} />
          <Route path="/:lang/styling-mock/animations" element={<StylingMockAnimations />} />
          <Route path="/styling-mock/navigation" element={<StylingMockNavigation />} />
          <Route path="/:lang/styling-mock/navigation" element={<StylingMockNavigation />} />
          
          {/* Fun Pages Routes */}
          <Route path="/fun" element={<FunIndex />} />
          <Route path="/:lang/fun" element={<FunIndex />} />
          <Route path="/fun/wichteln/:code" element={<Wichteln />} />
          <Route path="/:lang/fun/wichteln/:code" element={<Wichteln />} />
        
        {/* Protected routes */}
        <Route 
          path="/dashboard" 
          element={
                <PageGuards>
                  {/* TODO: Create Dashboard component */}
                <div>Dashboard - Coming Soon</div>
                </PageGuards>
              } />

        <Route 
          path="/:lang/dashboard" 
          element={
                <PageGuards>
                  {/* TODO: Create Dashboard component */}
                <div>Dashboard - Coming Soon</div>
                </PageGuards>
              } />



        {/* ============================================================================
            AUTHENTICATED PAGES REMOVED - ResourceManagement moved to platform-auth
            ============================================================================ */}

        {/* TODO: ResourcePage is app-specific, moved to platform-auth - all ResourcePage routes removed */}


        {/* User & Access Management Routes */}
          {/* Payment profile routes - moved to platform-auth */}
          {/* <Route 
            path="/user-tenant-mappings" 
            element={
                <PageGuards>
                  <PaymentProfilePage />
                </PageGuards>
              } />

          <Route 
            path="/payment-profile/:costPoolId" 
            element={
                <PageGuards>
                  <PaymentProfilePage />
                </PageGuards>
              } />

          <Route 
            path="/:lang/payment-profile" 
            element={
                <PageGuards>
                  <PaymentProfilePage />
                </PageGuards>
              } />

          <Route 
            path="/:lang/payment-profile/:costPoolId" 
            element={
                <PageGuards>
                  <PaymentProfilePage />
                </PageGuards>
              } /> */}

        {/* Notification routes - moved to platform-auth */}
        {/* <Route 
          path="/notifications" 
          element={<Navigate to="/" replace />}
              <CommunicationsCenter />
            
          } 
        />

        <Route 
          path="/:lang/notifications" 
          element={<Navigate to="/" replace />}
        /> */}

        {/* Communications Center routes - moved to platform-auth */}
        {/* <Route 
          path="/communications" 
          element={<Navigate to="/" replace />}
        />

        <Route 
          path="/:lang/communications" 
          element={<Navigate to="/" replace />}
        />

        <Route 
          path="/preferences/notifications" 
          element={<Navigate to="/communications?tab=settings" replace />}
        />

        <Route 
          path="/:lang/preferences/notifications" 
          element={<Navigate to="/communications?tab=settings" replace />}
        /> */}

        {/* Confidential pages - moved to platform-auth */}
        {/* <Route 
          path="/partner-pricing" 
          element={<Navigate to="/" replace />}
              <ConfidentialLayout>
                <PartnerPricing />
              </ConfidentialLayout>
            
          } 
        />

        <Route 
          path="/:lang/partner-pricing" 
          element={<Navigate to="/" replace />}
              <ConfidentialLayout>
                <PartnerPricing />
              </ConfidentialLayout>
            
          } 
        />

        <Route 
          path="/partner-pitch-deck" 
          element={<Navigate to="/" replace />}
              <ConfidentialLayout>
                <PartnerPitchDeck />
              </ConfidentialLayout>
            
          } 
        />

        <Route 
          path="/:lang/partner-pitch-deck" 
          element={<Navigate to="/" replace />}
              <ConfidentialLayout>
                <PartnerPitchDeck />
              </ConfidentialLayout>
            
          } 
        /> */}
        </Routes>
      </main>
      
      {/* Mobile Bottom Navigation - Context-aware */}
      {!isConfidentialPage && (
        <BottomNavigationWrapper
          viewMode={viewMode}
          onSignIn={handleSignIn}
        />
      )}
      
      {/* Technical Interface - Hidden on bot management, mobile, and confidential pages */}
      {!isConfidentialPage && !isBotManagementPage && !isMobile && <Logger sidebarOpen={sidebarOpen} />}
      
      {!isConfidentialPage && <Footer />}
      
      {/* User Type Selection Modal - Shows when user first logs in */}
      {showUserTypeSelection && (
        <UserTypeSelectionModal 
          onSelect={handleUserTypeSelection}
        />
      )}

      {/* Onboarding and ForcedLegalAgreementModal removed — only exist in platform-auth */}
          </div>
        </I18nProvider>
      </ProductProvider>
    </PublicTenantProvider>
  );
}

// Gate: render nothing until after first paint so useMsal/Router/useLocation don't run in first commit.
// Prevents "Maximum call stack size exceeded" (N9/Ay recursion) when providers + Router run in same commit.
function DeferredProvidersAndRouter() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);
  if (!ready) return null;
  return (
    <PublicUserTypeProvider>
      <PublicProfileProvider>
        <PublicAuthProvider>
          <ToastContainer>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true
              }}
            >
              <AppContent />
            </Router>
          </ToastContainer>
        </PublicAuthProvider>
      </PublicProfileProvider>
    </PublicUserTypeProvider>
  );
}

function App() {
  if (typeof window !== 'undefined') console.warn('[platform-public] v2 App render');
  // Display startup logo once on mount
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' || (typeof window !== 'undefined' && window.location && window.location.search && window.location.search.includes('ppdebug'))) {
      console.log('[PP-DBG] 4 App useEffect (displayStartupLogo) start');
    }
    displayStartupLogo();
  }, []); // Empty dependency array = run once on mount

  return (
    <ErrorBoundary>
      <DarkModeProvider>
        <DeferredProvidersAndRouter />
      </DarkModeProvider>
    </ErrorBoundary>
  );
}

export default App;
